<script lang="ts">
  import { onMount } from 'svelte';
  import {
    AURAS,
    CLASSES,
    CLASS_SLOTS,
    UTILITY,
    UTILITY_SLOTS,
    abilityDef,
    type AbilityId,
  } from '@arena/shared';
  import { click } from '../game/Sfx';
  import { app } from '../lib/net.svelte';
  import {
    barFor,
    keyLabel,
    settings,
    utilityBar,
  } from '../lib/settings.svelte';
  import { SPELL_DRAG } from './hud/ActionBar.svelte';

  let {
    locked,
    onhover,
    onclose,
  }: {
    /** In a match (but not practice) the spells are set; only their order can change. */
    locked: boolean;
    onhover: (id: AbilityId | null) => void;
    onclose: () => void;
  } = $props();

  const key = (binding: string) => keyLabel(settings.keys[binding]?.[0], true);
  /** Each section with the bar its spells go on; utility keys follow the class slots. */
  const sections = $derived([
    ...app.classes.map((id) => ({
      title: CLASSES[id].name,
      color: CLASSES[id].color,
      innate: AURAS[CLASSES[id].innate],
      ids: CLASSES[id].abilities,
      bar: barFor(id),
      first: 1,
    })),
    {
      title: 'Utility',
      color: undefined,
      innate: undefined,
      ids: UTILITY,
      bar: utilityBar(),
      first: CLASS_SLOTS + 1,
    },
  ]);

  // A removed entry never fires mouseleave.
  onMount(() => () => onhover(null));

  function onKey(event: KeyboardEvent) {
    if (event.code !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    click();
    onclose();
  }
</script>

<svelte:window onkeydowncapture={onKey} />

<section class="panel spellbook">
  <header>
    <h2>Spellbook</h2>
    {#each app.classes as id (id)}<span style="color: {CLASSES[id].color}"
        >{CLASSES[id].name}</span
      >{/each}
    <button class="close" onclick={onclose} aria-label="Close">×</button>
  </header>

  <div class="content">
    <p class="muted small">
      {locked
        ? 'Your spells are set for this match. You can still rearrange your bars.'
        : `Drag a spell onto your action bars: ${CLASS_SLOTS} for each class (the small row is the class you're not playing) and ${UTILITY_SLOTS} utility spells both share, each in one slot. Only spells on them can be cast in a match.`}
    </p>
    <ul>
      <li
        class="on-bar fixed"
        onmouseenter={() => onhover('swap')}
        onmouseleave={() => onhover(null)}
      >
        <img src="/icons/swap.webp" alt="" draggable="false" />
        <strong>{abilityDef('swap').name}</strong>
        <kbd>{key('swap')}</kbd>
      </li>
    </ul>
    {#each sections as { title, color, innate, ids, bar, first } (title)}
      <h3 style:color>{title}</h3>
      {#if innate}<p class="muted small innate">
          {innate.name}: {innate.description}
        </p>{/if}
      <ul>
        {#each ids as id (id)}
          {@const slot = bar.indexOf(id)}
          <li
            class:on-bar={slot >= 0}
            class:locked={locked && slot < 0}
            draggable={!locked || slot >= 0}
            ondragstart={(e) => e.dataTransfer?.setData(SPELL_DRAG, id)}
            onmouseenter={() => onhover(id)}
            onmouseleave={() => onhover(null)}
          >
            <img src="/icons/{id}.webp" alt="" draggable="false" />
            <strong>{abilityDef(id).name}</strong>
            {#if slot >= 0}<kbd
                >{key(`action${slot + first}`) || slot + first}</kbd
              >{/if}
          </li>
        {/each}
      </ul>
    {/each}
  </div>
</section>

<style>
  .spellbook {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: grid;
    grid-template-rows: auto minmax(0, 1fr);
    width: min(560px, calc(100% - 24px));
    max-height: min(560px, calc(100% - 260px));
    padding: 0;
    pointer-events: auto;
    background: var(--panel-solid);
    border-color: var(--gold);
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.6);
    font-size: 14px;
  }
  header {
    display: flex;
    gap: 12px;
    align-items: center;
    padding: 10px 18px;
    border-bottom: 1px solid var(--border);
  }
  header h2 {
    margin: 0;
  }
  .close {
    margin-left: auto;
    padding: 0 8px;
    font-size: 18px;
  }
  .content {
    overflow-y: auto;
    padding: 6px 18px 14px;
  }
  h3 {
    margin: 12px 0 6px;
    font-size: 13px;
    color: var(--muted);
  }
  .innate {
    margin: -2px 0 6px;
  }
  ul {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 6px;
    margin: 0;
    padding: 0;
    list-style: none;
  }
  li {
    display: flex;
    gap: 10px;
    align-items: center;
    padding: 4px 10px 4px 4px;
    border: 1px solid var(--border);
    border-radius: 4px;
    background: #13151c;
    cursor: grab;
  }
  li.on-bar {
    border-color: var(--gold);
    background: rgba(230, 192, 106, 0.08);
  }
  li.fixed {
    cursor: default;
  }
  li.locked {
    opacity: 0.5;
    cursor: default;
  }
  img {
    width: 40px;
    height: 40px;
    border: 2px solid #6b5a35;
    border-radius: 4px;
  }
  kbd {
    margin-left: auto;
    padding: 2px 7px;
    font: inherit;
    font-size: 12px;
    font-weight: bold;
    color: #fff3d6;
    background: linear-gradient(#3b3428, #221d15);
    border: 1px solid #6b5a35;
    border-bottom-width: 3px;
    border-radius: 4px;
  }
  .muted {
    color: var(--muted);
  }
  .small {
    font-size: 12px;
  }
</style>
